import { useCallback, useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UUID } from "crypto";
import { Box, MenuItem, Paper, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { signIn } from "aws-amplify/auth";
import { Badge, CountrySelect, FieldWrapper, PageHeader, SectionHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import {
  setAccountUserPhone,
  setLoanId,
  setClientDetailsId,
  setId_application,
  set_Id_applicationCondition,
  setPersonalData,
  setCompanyData,
  setLegalRepresentative,
  setLoanStatus,
  setTaxProductId,
  setTaxSystemType,
} from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useRegisterUserMutation, usePatchUserMutation, useRequestPhoneCodeMutation } from "@api/onboarding";
import { useLazyGetSessionInfoQuery } from "@api/session";
import { useLazyGetPersonTypeQuery } from "@api/catalogs";
import {
  useLazyGetPersonByIdQuery,
  useLazyGetPersonsQuery,
  usePatchPersonMutation,
  usePostPersonMutation,
} from "@api/personasApi";
import { usePatchCuentaMutation } from "@api/account";
import { useLazyGetLeasingListQuery, usePatchLeasingMutation, usePostLeasingMutation } from "@api/leasing";
import { LeasingMedicalEquipmentDetails, LeasingPost, LeasingStructure } from "@interfaces/LeasingTypes";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { PayloadRegisterUser, PayloadPersonPatch, PayloadRequestPhoneCode } from "@interfaces/request";
import { CatalogPersonType } from "@interfaces/catalogs";
import { ApiError, TaxSystemConfigResponse } from "@interfaces/response";
import { PATHS_COTIZADORES_V2, PATHS_TOFU_V2, PERSON_CATALOG, STATUS_CATALOG } from "@helpers/constantes";
import { transformTitleCase } from "@helpers/funciones";
import { NAME, LAST_NAMES, PHONE_NUMBER, RFC_PFAE, RFC_PM } from "@utils/expreciones";

type FormValues = {
  rfc: string;
  name: string;
  first_lastname: string;
  second_lastname: string;
  /** Razón social (solo PM). */
  company_name: string;
  /** Nombre del consultorio, clínica o laboratorio; se guarda en el `details` del leasing. */
  consultorio: string;
  local_atencion: string;
  person_type_id: string;
  country_code: string;
  phone: string;
};

/** "El local donde atiendes es": se guarda en el `details` del leasing (`premises_type`). */
const LOCAL_ATENCION_OPCIONES = [
  { value: "renta-tercero", label: "Rentado a un tercero" },
  { value: "renta-socio", label: "Rentado a un socio o familiar" },
  { value: "propio", label: "Propio, sin hipoteca" },
  { value: "hipoteca", label: "Propio, con hipoteca" },
];

/**
 * "Quién llena esta solicitud" por régimen, en el orden del prototipo (`MC_Datos`): en PFAE el titular del RFC o el
 * administrador del consultorio; en PM el representante legal, el socio o accionista, el director o administrador y
 * el médico responsable.
 */
const ROL_CODES_PFAE = [PERSON_CATALOG.titular_rfc.code, PERSON_CATALOG.administrador_consultorio.code];
const ROL_CODES_PM = [
  PERSON_CATALOG.representante_legal.code,
  PERSON_CATALOG.socio_accionista.code,
  PERSON_CATALOG.director_administrador.code,
  PERSON_CATALOG.medico_responsable.code,
];

const EMPTY_ID = "00000000-0000-0000-0000-000000000000";

/**
 * Tax system de Mercader según el RFC: 13 posiciones = PFAE (`MER01`, el del cotizador) y 12 = PM (`MER02`). Se
 * decide al registrarse; después el RFC queda bloqueado, así que el régimen ya no cambia.
 */
const TAX_SYSTEM_POR_REGIMEN = { PFAE: "MER01", PM: "MER02" } as const;
type RegimenMercader = keyof typeof TAX_SYSTEM_POR_REGIMEN;
const getRegimenPorRfc = (rfc: string): RegimenMercader => (rfc.trim().length === 12 ? "PM" : "PFAE");
const esRfcPM = (rfc?: string) => getRegimenPorRfc(rfc ?? "") === "PM";

const validationSchema = yup.object().shape({
  rfc: yup
    .string()
    .required("Campo requerido")
    // PFAE (13 posiciones) o PM (12). Primero la longitud y luego el formato, para que cada caso tenga su mensaje
    // (QA PRA-03): un RFC con fecha inexistente (ej. mes 13) no debe decir que le faltan posiciones.
    .test(
      "rfc-longitud",
      "Revisa el RFC: debe tener 12 posiciones (persona moral) o 13 (persona física con actividad empresarial).",
      (value) => !value || value.length === 12 || value.length === 13,
    )
    .test(
      "rfc-formato",
      "Revisa el RFC: el formato o la fecha no son válidos.",
      (value) => !value || RFC_PFAE.test(value) || RFC_PM.test(value),
    ),
  // PFAE captura el nombre del titular; PM, la razón social (como `MC_Datos` del prototipo).
  name: yup.string().when("rfc", {
    is: esRfcPM,
    then: (schema) => schema,
    otherwise: (schema) =>
      schema.required("Campo requerido").matches(new RegExp(NAME), "Nombre inválido, revisa el formato"),
  }),
  first_lastname: yup.string().when("rfc", {
    is: esRfcPM,
    then: (schema) => schema,
    otherwise: (schema) =>
      schema
        .required("Campo requerido")
        .matches(new RegExp(LAST_NAMES), "Apellido paterno inválido, revisa el formato"),
  }),
  second_lastname: yup.string().when("rfc", {
    is: esRfcPM,
    then: (schema) => schema,
    otherwise: (schema) => schema.matches(new RegExp(LAST_NAMES), "Apellido materno inválido, revisa el formato"),
  }),
  company_name: yup.string().when("rfc", {
    is: esRfcPM,
    then: (schema) => schema.trim().required("Campo requerido"),
    otherwise: (schema) => schema,
  }),
  consultorio: yup.string().trim(),
  local_atencion: yup.string().required("Campo requerido"),
  person_type_id: yup.string().required("Campo requerido"),
  country_code: yup.string().required("Campo requerido"),
  phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
});

/**
 * `MC_Datos` del diseño de Mercader ("Tu práctica médica"): reemplaza a `RegistroDatosPersonales`
 * para Mercader. Acepta RFC de PFAE (13 posiciones) o de PM (12); según el RFC se guarda con el tax system de
 * ese régimen (`TAX_SYSTEM_POR_REGIMEN`).
 * La lógica de guardado es la del legacy `tofu/pm/RegistroDatosPersonales` para PFAE: registra al
 * usuario con RFC y nombre comercial, inicia sesión, da de alta a la persona y pide el código SMS.
 */
export const RegistroDatosPersonalesMercader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const companyData = useSelector((state: RootState) => state.register.company);

  const [registerUser] = useRegisterUserMutation();
  const [patchAccount] = usePatchCuentaMutation();
  const [patchUser] = usePatchUserMutation();
  const [requestPhoneCode] = useRequestPhoneCodeMutation();
  const [getPersonTypesTrigger] = useLazyGetPersonTypeQuery();
  const [getSessionInfo] = useLazyGetSessionInfoQuery();
  const [getPersonsByLoanId] = useLazyGetPersonsQuery();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [saveLegalRepresentative] = usePostPersonMutation();
  const [patchPerson] = usePatchPersonMutation();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  // Registro de leasing del equipo médico (mismos servicios que el arrendamiento legacy).
  const [postLeasing] = usePostLeasingMutation();
  const [getLeasingList] = useLazyGetLeasingListQuery();
  const [patchLeasing] = usePatchLeasingMutation();

  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  // Catálogo completo de tipos de persona; las opciones de "Quién llena" se arman según el régimen del RFC.
  const [personTypesAll, setPersonTypesAll] = useState<CatalogPersonType[]>([]);
  const [personTypeIdsForRep, setPersonTypeIdsForRep] = useState<string[]>([]);
  const [personId, setPersonId] = useState("");
  // Los dos tax systems de Mercader (PFAE y PM) se cargan al entrar; al guardar se usa el del régimen del RFC.
  const [taxSystemsPorRegimen, setTaxSystemsPorRegimen] = useState<
    Partial<Record<RegimenMercader, TaxSystemConfigResponse>>
  >({});

  /**
   * `crear_cuenta` necesita los query params de la cotización: se regresa por historial y, si no lo
   * hay (recarga o enlace directo), al cotizador del tenant. Igual que `RegistroDatosPersonales`.
   */
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate(PATHS_COTIZADORES_V2.credito_simple.path);
  };

  // Solo PFAE: el titular se da de alta como persona aquí. En PM el representante legal se registra después.
  const createPersonOrUpdatePerson = async (loanApplicationId: string, values: FormValues) => {
    if (getRegimenPorRfc(values.rfc) !== "PFAE") return;

    const payloadPerson: PayloadPersonPatch = {
      person_types: personTypeIdsForRep,
      name: values.name,
      last_name: values.first_lastname,
      last_name_2: values.second_lastname,
      loan_application_id: loanApplicationId,
      code_phone_number: values.country_code,
      phone_number: values.phone,
      email: accountUser.email,
      rfc: values.rfc,
      is_shareholder: true,
    };

    if (personId) {
      await patchPerson({ personId, body: payloadPerson }).unwrap();
    } else {
      const responseCreate = await saveLegalRepresentative({ body: payloadPerson }).unwrap();
      setPersonId(responseCreate.data.id);
      dispatch(setLegalRepresentative(responseCreate.data));
    }
  };

  /**
   * Leasing del equipo médico, con el mismo patrón que el arrendamiento legacy: si la solicitud ya
   * tiene uno se actualiza (`patchLeasing`) y si no se crea (`postLeasing`). Lo que no tiene campo
   * propio va en `details` (JSON): la descripción del equipo (del cotizador) y el tipo de local.
   */
  const saveLeasing = async (loanApplicationId: string, values: FormValues) => {
    const leasing: LeasingPost<LeasingMedicalEquipmentDetails> = {
      loan_application_id: loanApplicationId,
      quantity: 1,
      // Valor del equipo sin IVA que se cotizó.
      estimated_value: application.loan_amount,
      requested_advance_percentage: application.advance_percentage || 0,
      // Arrendamiento puro: sin valor residual.
      requested_residual_percentage: 0,
      details: {
        equipment_description: application.equipment_description ?? null,
        premises_type: values.local_atencion,
        clinic_name: values.consultorio.trim() || null,
      },
    };

    const [existing] = await getLeasingList(loanApplicationId).unwrap();
    if (existing?.id) {
      await patchLeasing({ id_solicitud: loanApplicationId, vehicleId: existing.id, data: leasing }).unwrap();
    } else {
      await postLeasing({ id_solicitud: loanApplicationId, data: { leasings_list: [leasing] } }).unwrap();
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      rfc: companyData.rfc || "",
      name: clientDetails.name || "",
      first_lastname: clientDetails.first_lastname || "",
      second_lastname: clientDetails.second_lastname || "",
      company_name: companyData.name || "",
      consultorio: "",
      local_atencion: "",
      person_type_id: clientDetails.person_type_id || "",
      country_code: accountUser.code_phone || "+52",
      phone: accountUser.phone || "",
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      const rfc = values.rfc.trim();
      const regimen = getRegimenPorRfc(rfc);
      // PM no captura nombre ni apellidos (el representante legal se registra después).
      if (regimen === "PM") {
        values = { ...values, name: "", first_lastname: "", second_lastname: "" };
      }
      // `company_name` es la razón social: en PM la capturada y en PFAE el nombre completo del titular.
      const companyName =
        regimen === "PM"
          ? values.company_name.trim()
          : [values.name, values.first_lastname, values.second_lastname]
              .map((parte) => parte.trim())
              .filter(Boolean)
              .join(" ");
      // Datos del titular PFAE en la cuenta, como `tofu/pm/DatosEmpresaPfae`. Los usa la consulta a Buró
      // (`NipBuro`) y `getSessionInfo` los vuelve a cargar en `company` al retomar la solicitud. En PM no aplican.
      const pfaeAccountData =
        regimen === "PFAE"
          ? {
              is_pfae: true,
              pfae_name: values.name,
              pfae_last_name: values.first_lastname,
              pfae_last_name_2: values.second_lastname,
              country_code_legal_representative: values.country_code,
              phone_legal_representative: values.phone,
              email_legal_representative: accountUser.email,
            }
          : { is_pfae: false };

      try {
        setSubmitting(true);

        // Si ya existe un borrador (el usuario vuelve a este paso), actualizamos en vez de dar de alta.
        if (clientDetails.id !== EMPTY_ID) {
          await patchAccount({
            accountId: clientDetails.id,
            body: {
              name: values.name,
              last_name: values.first_lastname,
              last_name_2: values.second_lastname,
              person_type_id: values.person_type_id,
              company_rfc: rfc,
              company_name: companyName,
              country_code: values.country_code,
              cell_phone_number: values.phone,
              ...pfaeAccountData,
            },
          }).unwrap();

          await patchUser({
            id: String(accountUser.id),
            data: {
              rol_detail: "",
              rol_id: values.person_type_id,
              phone: `${values.country_code}${values.phone}`,
            },
          }).unwrap();

          await createPersonOrUpdatePerson(application.id, values);
          await saveLeasing(application.id, values);

          dispatch(
            setPersonalData({
              id: clientDetails.id,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              person_type_id: values.person_type_id,
              rol_detail: "",
              cell_phone_verified: clientDetails.cell_phone_verified,
              country_code: "",
              phone: "",
            }),
          );
          dispatch(
            setCompanyData({
              id: clientDetails.id,
              rfc,
              name: companyName,
              legal_name: "",
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_correct_ciec: false,
              ...pfaeAccountData,
            }),
          );
          dispatch(
            setAccountUserPhone({
              id: accountUser.id,
              phone_full: `${values.country_code}${values.phone}`,
              code_phone: values.country_code,
              phone: values.phone,
            }),
          );
        } else {
          // Tax system del régimen del RFC (`MER01` / `MER02`), ya cargado al entrar.
          const taxSystem = taxSystemsPorRegimen[regimen];
          if (!taxSystem) {
            throw new Error(`No se encontró la configuración del tax system ${TAX_SYSTEM_POR_REGIMEN[regimen]}`);
          }

          const payloadRegisterUser: PayloadRegisterUser = {
            username: accountUser.email,
            email: accountUser.email,
            password: accountUser.password,
            phone: `${values.country_code}${values.phone}`,
            rol_detail: "",
            rol_id: values.person_type_id,
            loan_application: {
              product_id: taxSystem.product_id as UUID,
              tax_systems_id: taxSystem.id,
              source_code: application.source_code,
              financed_services_id: application.financed_services_id,
              guarantee_type_id: application.guarantee_type_id,
              description_guarantee: application.description_guarantee,
              guarantee_types: application.guarantee_types?.length ? application.guarantee_types : null,
              convenio_id: application.convenio_id ?? null,
              payroll_agreement_id: application.convenio_id ?? null,
              leasing_type_id: application.leasing_type_id ?? null,
              quoter_details: application.quoter_details ?? null,
            },
            application_condition: {
              requested_amount: application.loan_amount,
              requested_term: application.loan_term,
              selected_amount: application.loan_amount,
              selected_term: application.loan_term,
              approved_max_term: taxSystem.maxTerm,
              approved_min_term: taxSystem.minTerm,
              loan_amount: application.loan_amount,
              approved_amount: taxSystem.maxAmount,
              selected_advance_percentage: application.advance_percentage || null,
              selected_residual_percentage: application.residual_percentage || null,
              requested_advance_percentage: application.advance_percentage || null,
              requested_residual_percentage: application.residual_percentage || null,
            },
            client_details: {
              name: values.name,
              last_name: values.first_lastname,
              last_name_2: values.second_lastname,
              person_type_id: values.person_type_id,
              tax_systems_id: taxSystem.id,
              is_previous_client: clientDetails.is_previous_client,
              company_rfc: rfc,
              company_name: companyName,
              country_code: values.country_code,
              cell_phone_number: values.phone,
            },
          };

          const responseRegisterUser = await registerUser(payloadRegisterUser).unwrap();

          await signIn({ username: accountUser.email, password: accountUser.password });
          await getSessionInfo(responseRegisterUser.data.sso_id).unwrap();

          // `registerUser` no recibe los datos PFAE en `client_details`: se guardan aparte, ya con sesión.
          await patchAccount({
            accountId: responseRegisterUser.data.client_details.id,
            body: pfaeAccountData,
          }).unwrap();

          dispatch(
            setAccountUserPhone({
              id: responseRegisterUser.data.id,
              phone_full: `${values.country_code}${values.phone}`,
              code_phone: values.country_code,
              phone: values.phone,
            }),
          );
          dispatch(setLoanId(responseRegisterUser.data.loan_application.id));
          dispatch(setClientDetailsId(responseRegisterUser.data.client_details.id));
          dispatch(setId_application(Number(responseRegisterUser.data.loan_application.id_application)));
          dispatch(set_Id_applicationCondition(String(responseRegisterUser.data.application_condition.id)));
          dispatch(
            setPersonalData({
              id: responseRegisterUser.data.client_details.id,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              person_type_id: values.person_type_id,
              rol_detail: "",
              cell_phone_verified: false,
              country_code: "",
              phone: "",
            }),
          );
          dispatch(
            setCompanyData({
              id: responseRegisterUser.data.client_details.id,
              rfc,
              name: companyName,
              legal_name: "",
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_correct_ciec: false,
              ...pfaeAccountData,
            }),
          );

          await createPersonOrUpdatePerson(responseRegisterUser.data.loan_application.id, values);
          await saveLeasing(responseRegisterUser.data.loan_application.id, values);

          // El store queda con el tax system del régimen del RFC: lo usan las pantallas siguientes (textos legales,
          // decisión de ingresos).
          dispatch(
            setTaxProductId({
              tax_system_id: taxSystem.id as UUID,
              product_id: taxSystem.product_id as UUID,
              source_code: application.source_code,
              type: taxSystem.code,
            }),
          );
          dispatch(setTaxSystemType(regimen));
        }

        // Igual que `RegistroDatosPersonales`: el código SMS se pide si el teléfono no está verificado y es
        // nuevo o cambió; si no, la validación de teléfono reusa el código ya enviado.
        if (!clientDetails.cell_phone_verified) {
          const payloadRequestPhoneCode: PayloadRequestPhoneCode = {
            country_code: values.country_code,
            phone_number: values.phone,
          };
          if (clientDetails.phone === "" || clientDetails.phone !== values.phone) {
            await requestPhoneCode(payloadRequestPhoneCode).unwrap();
          }
        }

        dispatch(setLoanStatus(STATUS_CATALOG.validacion_telefono.code));
        navigate(PATHS_TOFU_V2.validacionTelefono.path);
      } catch (error) {
        const api_error = error as ApiError;

        // Mismos códigos y campos que el legacy (`tofu/pm/RegistroDatosPersonales`).
        if (api_error.status && api_error.data) {
          if (api_error.data.code === "101") {
            formik.setFieldError("name", "Correo inválido, dominio no permitido, intenta con otro correo");
            return;
          }
          if (api_error.data.code === "002") {
            formik.setFieldError("phone", "El teléfono ya se encuentra registrado, intenta con otro número");
            return;
          }
          if (api_error.data.code === "001") {
            formik.setFieldError("name", "El correo ya se encuentra registrado, intenta con otro correo");
            return;
          }
          if (api_error.data.code === "000") {
            formik.setFieldError("rfc", `Existe una cuenta con RFC - ${values.rfc}`);
            return;
          }
        }

        Sentry.captureException(error, {
          extra: { component: "RegistroDatosPersonalesMercader-submit", values: formik.values },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue } = formik;

  const fetchInitialData = useCallback(async () => {
    if (!application.tax_system_type || !accountUser.email) return;

    try {
      setIsLoadingInitial(true);

      const [result, taxSystemPfae, taxSystemPm] = await Promise.all([
        getPersonTypesTrigger().unwrap(),
        getTaxSystemConfig(TAX_SYSTEM_POR_REGIMEN.PFAE).unwrap(),
        getTaxSystemConfig(TAX_SYSTEM_POR_REGIMEN.PM).unwrap(),
      ]);
      setTaxSystemsPorRegimen({ PFAE: taxSystemPfae, PM: taxSystemPm });

      setPersonTypesAll(result);

      const accionistaMayoritario = result.find((item) => item.code === PERSON_CATALOG.accionista_mayoritario.code);
      const representanteLegal = result.find((item) => item.code === PERSON_CATALOG.representante_legal.code);
      const ids: string[] = [];
      if (accionistaMayoritario?.id) ids.push(String(accionistaMayoritario.id));
      if (representanteLegal?.id) ids.push(String(representanteLegal.id));
      setPersonTypeIdsForRep(ids);

      // Si el usuario vuelve a este paso, la persona ya existe: se actualiza en vez de duplicarla.
      if (!application.id || application.id === EMPTY_ID) return;
      const persons = await getPersonsByLoanId({ loanApplicationId: application.id }).unwrap();
      if (persons.length > 0) {
        const person = await getPersonById(persons[0].id).unwrap();
        setPersonId(String(person.id));
      }

      // El tipo de local se guardó en el `details` del leasing. Va aparte: si falla, el resto de la
      // carga ya quedó hecha, pero se avisa con el modal de error de servicio.
      try {
        const [leasing] = (await getLeasingList(
          application.id,
        ).unwrap()) as LeasingStructure<LeasingMedicalEquipmentDetails>[];
        if (leasing?.details?.premises_type) {
          setFieldValue("local_atencion", leasing.details.premises_type);
        }
        if (leasing?.details?.clinic_name) {
          setFieldValue("consultorio", leasing.details.clinic_name);
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "RegistroDatosPersonalesMercader-leasing" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      }
    } catch (error) {
      // Carga inicial con respaldo (como el legacy): sin catálogo el select queda vacío, sin modal.
      Sentry.captureException(error, { extra: { component: "RegistroDatosPersonalesMercader-initial_fetch" } });
    } finally {
      setIsLoadingInitial(false);
    }
  }, [
    application.tax_system_type,
    application.id,
    accountUser.email,
    getPersonTypesTrigger,
    getTaxSystemConfig,
    getPersonsByLoanId,
    getPersonById,
    getLeasingList,
    setFieldValue,
    dispatch,
  ]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const bloqueado = formik.isSubmitting || isLoadingInitial;
  // Ya registrado, el RFC no se puede cambiar: con él se eligió el tax system (PFAE o PM) de la solicitud.
  const yaRegistrado = clientDetails.id !== EMPTY_ID;
  const rfcValido = !formik.errors.rfc && [12, 13].includes(formik.values.rfc.length);
  const rfcEsPM = getRegimenPorRfc(formik.values.rfc) === "PM";
  // Los roles de Mercader traen `use_for_rol: false` y no siempre `tax_system_type`, por eso se filtran por código
  // y no como en `RegistroDatosPersonales`; van en el orden del diseño.
  const personTypeCatalog = useMemo(
    () =>
      (rfcEsPM ? ROL_CODES_PM : ROL_CODES_PFAE)
        .map((code) => personTypesAll.find((item) => item.code === code))
        .filter((item): item is CatalogPersonType => !!item),
    [rfcEsPM, personTypesAll],
  );

  // Si cambia el régimen y el rol elegido ya no aplica (ej. representante legal en PFAE), se limpia.
  useEffect(() => {
    const elegido = formik.values.person_type_id;
    if (elegido && personTypeCatalog.length > 0 && !personTypeCatalog.some((item) => item.id === elegido)) {
      setFieldValue("person_type_id", "");
    }
  }, [personTypeCatalog, formik.values.person_type_id, setFieldValue]);
  // Como en el diseño, el error del RFC aparece en cuanto se teclean 12 posiciones (las de una PM).
  const rfcError = formik.touched.rfc || formik.values.rfc.length >= 12 ? formik.errors.rfc || "" : "";

  return (
    <Box>
      <PageHeader
        eyebrow="Tu práctica médica"
        title="Cuéntanos quién arrienda el equipo"
        subtitle="Arrendamos a médicos como persona física con actividad empresarial y a empresas del sector salud. La renta se factura al RFC del arrendatario."
      />

      {/* Sin `gap`: el espacio entre secciones lo dan los márgenes de `SectionHeader` (divider + label); con gap se
          sumaba a cada uno y quedaba un hueco grande antes de "Contacto". */}
      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column" }}>
        <SectionHeader label="Quién factura" first />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <FieldWrapper
            label="RFC con el que facturas"
            full
            required
            hint={
              rfcError
                ? undefined
                : "RFC de persona moral (12 posiciones) o de persona física con actividad empresarial (13)."
            }
            error={rfcError}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="rfc"
              placeholder="Ej. ROMC850312K47"
              value={formik.values.rfc}
              onChange={(e) => setFieldValue("rfc", e.target.value.toUpperCase().replace(/[\s-]/g, ""))}
              onBlur={formik.handleBlur}
              disabled={bloqueado || yaRegistrado}
              required
              inputProps={{
                "aria-required": true,
                maxLength: 13,
                style: { fontVariantNumeric: "tabular-nums", letterSpacing: "0.04em" },
                "data-testid": "registro-mercader-rfc-input",
              }}
            />
          </FieldWrapper>

          {rfcValido && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <Badge tone="ok">{rfcEsPM ? "Persona moral" : "Persona física con actividad empresarial"}</Badge>
              <Typography variant="body2" color="tokens.textSecondary">
                {rfcEsPM
                  ? "La empresa es la arrendataria; firma su representante legal."
                  : "Tú eres el arrendatario y firmas con tu propio RFC."}
              </Typography>
            </Box>
          )}

          {rfcEsPM ? (
            <FieldWrapper
              label="Razón social"
              full
              required
              error={(formik.touched.company_name && formik.errors.company_name) || ""}
            >
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="company_name"
                placeholder="Ej. Centro Médico Roble, S.A. de C.V."
                value={formik.values.company_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={bloqueado}
                required
                inputProps={{
                  maxLength: 150,
                  "aria-required": true,
                  "data-testid": "registro-mercader-razon-social-input",
                }}
              />
            </FieldWrapper>
          ) : (
            // El diseño pide el nombre completo del titular en un campo; backend lo recibe separado, así que van
            // los tres en la misma línea bajo esa leyenda.
            <FieldWrapper label="Nombre completo del titular" full>
              <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "12px 14px" }}>
                <FieldWrapper label="Nombre(s)" required error={(formik.touched.name && formik.errors.name) || ""}>
                  <TextField
                    autoComplete="off"
                    size="lendia_input"
                    fullWidth
                    name="name"
                    placeholder="Carla"
                    value={formik.values.name}
                    onChange={(e) => setFieldValue("name", transformTitleCase(e.target.value))}
                    onBlur={formik.handleBlur}
                    disabled={bloqueado}
                    required
                    inputProps={{
                      maxLength: 100,
                      "aria-required": true,
                      "data-testid": "registro-mercader-nombre-input",
                    }}
                  />
                </FieldWrapper>
                <FieldWrapper
                  label="Apellido paterno"
                  required
                  error={(formik.touched.first_lastname && formik.errors.first_lastname) || ""}
                >
                  <TextField
                    autoComplete="off"
                    size="lendia_input"
                    fullWidth
                    name="first_lastname"
                    placeholder="Robles"
                    value={formik.values.first_lastname}
                    onChange={(e) => setFieldValue("first_lastname", transformTitleCase(e.target.value))}
                    onBlur={formik.handleBlur}
                    disabled={bloqueado}
                    required
                    inputProps={{
                      maxLength: 100,
                      "aria-required": true,
                      "data-testid": "registro-mercader-apellido-paterno-input",
                    }}
                  />
                </FieldWrapper>
                <FieldWrapper
                  label="Apellido materno"
                  optionalSuffix="(opcional)"
                  error={(formik.touched.second_lastname && formik.errors.second_lastname) || ""}
                >
                  <TextField
                    autoComplete="off"
                    size="lendia_input"
                    fullWidth
                    name="second_lastname"
                    placeholder="Mendoza"
                    value={formik.values.second_lastname}
                    onChange={(e) => setFieldValue("second_lastname", transformTitleCase(e.target.value))}
                    onBlur={formik.handleBlur}
                    disabled={bloqueado}
                    inputProps={{ maxLength: 100, "data-testid": "registro-mercader-apellido-materno-input" }}
                  />
                </FieldWrapper>
              </Box>
            </FieldWrapper>
          )}

          <FieldWrapper
            label="Nombre del consultorio, clínica o laboratorio"
            full
            optionalSuffix="(opcional)"
            hint="En caso de ser distinto al anterior."
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="consultorio"
              placeholder="Ej. Clínica Roble Imagen"
              value={formik.values.consultorio}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={bloqueado}
              inputProps={{ maxLength: 150, "data-testid": "registro-mercader-consultorio-input" }}
            />
          </FieldWrapper>

          <FieldWrapper
            label="El local donde atiendes es"
            full
            required
            hint="Nos ayuda a entender tu negocio."
            error={(formik.touched.local_atencion && formik.errors.local_atencion) || ""}
          >
            <TextField
              size="lendia_input"
              select
              fullWidth
              name="local_atencion"
              value={formik.values.local_atencion}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={bloqueado}
              required
              SelectProps={{ displayEmpty: true, inputProps: { "aria-required": true } }}
              inputProps={{ "data-testid": "registro-mercader-local-select" }}
            >
              <MenuItem value="" disabled>
                Selecciona…
              </MenuItem>
              {LOCAL_ATENCION_OPCIONES.map((opcion) => (
                <MenuItem key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </MenuItem>
              ))}
            </TextField>
          </FieldWrapper>
        </Box>

        <SectionHeader label="Contacto" />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <FieldWrapper
            label="Quién llena esta solicitud"
            full
            required
            error={(formik.touched.person_type_id && formik.errors.person_type_id) || ""}
          >
            <TextField
              size="lendia_input"
              select
              fullWidth
              name="person_type_id"
              value={formik.values.person_type_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={bloqueado}
              required
              SelectProps={{ displayEmpty: true, inputProps: { "aria-required": true } }}
              inputProps={{ "data-testid": "registro-mercader-rol-select" }}
            >
              <MenuItem value="" disabled>
                Selecciona…
              </MenuItem>
              {personTypeCatalog.length > 0 ? (
                personTypeCatalog.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay opciones disponibles
                </MenuItem>
              )}
            </TextField>
          </FieldWrapper>
          <FieldWrapper
            label="Teléfono celular"
            full
            required
            error={(formik.touched.phone && formik.errors.phone) || ""}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <CountrySelect
                value={formik.values.country_code}
                onChange={(value) => setFieldValue("country_code", value)}
                disabled={bloqueado || clientDetails.cell_phone_verified}
              />
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="phone"
                placeholder="55 1234 5678"
                value={formik.values.phone}
                onChange={(e) => setFieldValue("phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                onBlur={formik.handleBlur}
                disabled={bloqueado || clientDetails.cell_phone_verified}
                required
                inputProps={{ "aria-required": true, "data-testid": "registro-mercader-telefono-input" }}
              />
            </Box>
          </FieldWrapper>
        </Box>
      </Paper>

      <StepActions
        onBack={handleBack}
        onNext={formik.submitForm}
        nextDisabled={!formik.isValid || bloqueado}
        backTestId="registro-mercader-back-button"
        nextTestId="registro-mercader-continue-button"
      />
    </Box>
  );
};
